import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUnscramble } from '../utils/api';
import { DICTIONARIES, loadDict, saveDict } from '../utils/dicts';
import DictInfo from '../components/DictInfo';
import DefinableWord from '../components/DefinableWord';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

export default function WordUnscrambler() {
  const [word, setWord]       = useState('');
  const [dict, setDict]       = useState(loadDict);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function solve(dictKey) {
    if (!word.trim()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiUnscramble(word, dictKey);
      setResults(data.results);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  function handleSolve(e) {
    e.preventDefault();
    solve(dict);
  }

  function handleDictChange(e) {
    const next = e.target.value;
    setDict(next);
    saveDict(next);
    if (results !== null) solve(next);   // re-run with the new dictionary
  }

  return (
    <div className="game-page">
      <PageSEO page="unscramble" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔍</span>
          <h1>Word Unscrambler</h1>
          <p>Enter your letters and find every word you can make from them — grouped by length.</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Scrambled Letters</label>
              <input
                className="big-input"
                value={word}
                onChange={e => setWord(e.target.value.replace(/[^a-zA-Z]/g,''))}
                placeholder="e.g. LEWOH"
                maxLength={15}
                autoFocus
              />
              <small className="text-muted fw-bold">Finds every word you can make from your {word.length} letters</small>
            </div>
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <label className="fw-bold mb-0">Dictionary</label>
                <DictInfo />
              </div>
              <select
                className="form-select"
                value={dict}
                onChange={handleDictChange}
                style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, borderRadius: 10, maxWidth: 360 }}
              >
                {DICTIONARIES.map(d => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="solve-btn" disabled={loading || !word}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Unscrambling…</> : '🔍 Unscramble'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            <p className="result-count mb-1">
              Found <strong>{results.length}</strong> word{results.length !== 1 ? 's' : ''} from <strong>{word.toUpperCase()}</strong>
            </p>
            {results.length > 0 && (
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>💡 Click any word to see its meaning.</p>
            )}
            {results.length === 0 ? (
              <div className="text-center py-3">
                <div style={{fontSize:'2.5rem'}}>😕</div>
                <p className="text-muted fw-bold">No words found from those letters. Check your spelling!</p>
              </div>
            ) : (
              Object.entries(
                results.reduce((groups, r) => {
                  (groups[r.length] = groups[r.length] || []).push(r);
                  return groups;
                }, {})
              )
                .sort((a, b) => b[0] - a[0])
                .map(([len, group]) => (
                  <div key={len} className="mb-3">
                    <div className="fw-bold mb-2" style={{ color: '#4f46e5', fontFamily: 'Nunito, sans-serif' }}>
                      {len}-Letter Words <span className="text-muted fw-normal">({group.length})</span>
                    </div>
                    <div className="words-cloud">
                      {group.map(r => (
                        <DefinableWord
                          key={r.word}
                          word={r.word}
                          dict={dict}
                          className="result-badge"
                          style={{fontSize:'1rem', padding:'0.4rem 1rem', textDecoration:'none'}}
                        >
                          {r.word.toUpperCase()}
                          <small className="ms-1 text-muted">({r.score}pts)</small>
                        </DefinableWord>
                      ))}
                    </div>
                  </div>
                ))
            )}
          </div>
        )}
      <SeoContent game="unscramble" />
      </div>
    </div>
  );
}
